import {
  Archive,
  ArrowLeftRight,
  CircleDollarSign,
  Edit2,
  FileText,
  FolderInput,
  Link2,
  Split,
  Trash2,
} from 'lucide-react';
import * as React from 'react';
import {useNavigate} from 'react-router-dom';
import {totalsByCurrency} from 'src/lib/currencyTotals';
import {useModals} from 'src/Modals';
import {
  PopoverMenuDivider,
  PopoverMenuGroupLabel,
} from 'src/PopoverMenu/views';
import PopoverMenuItem from 'src/PopoverMenuItem';
import type {BasicRawTransactionFragment} from '@transactions/graphql';
import type {Mutation} from 'src/apolloCache/updateRawTransactions';
import type {SelectionState} from 'src/useSelectionState';

type Options = {
  selectionState: SelectionState<BasicRawTransactionFragment>;
  onUpdate: (mutation: Mutation) => void;
  includeProcessed?: boolean;
  removeFullyConsumed?: boolean;
  accountId?: string;
};

/**
 * Everything a raw-transaction list can do with its selected rows: the
 * modals, what each needs of the selection, and how the list's cache
 * follows. The list header renders it as icon buttons and an overflow menu;
 * the Inbox card's selection bar renders the same menu, so both offer
 * exactly the same actions.
 */
export const useRawSelectionActions = ({
  selectionState,
  onUpdate,
  includeProcessed = false,
  removeFullyConsumed = false,
  accountId,
}: Options) => {
  const {selected} = selectionState;
  const sums = totalsByCurrency(selected);
  const selectedCount = selected.length;
  const hasSelected = selectedCount > 0;
  const singleSelected = selectedCount === 1;

  const modals = useModals();
  const navigate = useNavigate();

  // While any modal is up, the header's hotkeys (Escape included) stand down
  // and let the modal handle keys. Checked against the whole registry rather
  // than a curated list of the modals this header itself opens: the owning
  // page can open others over the same mounted header (e.g. the reconcile
  // tab's Record balance dialog), and a curated list drifts as modals are
  // added.
  const anyModalOpen = Object.values(modals).some((modal) => modal.state.open);

  // A fully consumed row has nothing left to allocate: the modal would open
  // with a zero allocation it can never submit, and the server rejects
  // consuming a zero-remaining raw either way — so every selected row must
  // still carry a remainder. Matters on lists that show processed rows (the
  // account All view, the reconcile tab). A null remainingAmount reads as
  // fully unconsumed, the same reading the server takes.
  const canCreateTransaction =
    hasSelected &&
    selected.every((r) => (r.remainingAmount ?? r.amount) !== 0);

  const openCreateTransactionModal = (options?: {expandBenefit?: boolean}) => {
    if (!canCreateTransaction) return;
    modals.createTransactionModal.openModal({
      onCreate: (data) => handleCreate(data),
      transactions: selected,
      expandBenefit: options?.expandBenefit,
    });
  };

  // A tap on an unallocated row: the create sheet for that row alone, with
  // the same list bookkeeping as creating from a selection.
  const openCreateForRow = (row: BasicRawTransactionFragment) => {
    modals.createTransactionModal.openModal({
      onCreate: (data) => handleCreate(data, [row]),
      transactions: [row],
    });
  };

  // Parking composes a new trackBalance account, an inverse raw transaction,
  // and a prepaid transfer — so the source must still have unconsumed amount
  // (null reads as fully unconsumed, the same reading the server takes), and
  // must be USD: createDepositoryAccount hardcodes the tracked account's
  // currency, and the modal permanently disables Create for a non-USD source.
  const canTrackCredit =
    singleSelected &&
    (selected[0].remainingAmount ?? selected[0].amount) !== 0 &&
    selected[0].currency === 'USD';

  const openDeleteRawTransactionModal = () => {
    if (selected.length === 0) return;
    modals.deleteRawTransactionModal.openModal({
      onDelete: handleDelete,
      rawTransactions: selected,
    });
  };

  // The list shows processed rows (e.g. the account page's All view), so a
  // fully consumed row stays visible with a zero remainder instead of
  // disappearing; lists that exclude processed rows drop it instead.
  const keepsConsumedRows = includeProcessed && !removeFullyConsumed;

  const handleCreate = (
    data: {
      rawTransactionIds: string[];
      updatedRawTransactions?: BasicRawTransactionFragment[];
    },
    // The rows the create consumed: the selection, unless a tap opened the
    // sheet on one row with nothing selected.
    rows: BasicRawTransactionFragment[] = selected,
  ) => {
    // A partially allocated raw transaction still has a remainder, so keep it
    // in the list (with its updated remainingAmount) instead of removing it.
    const updated = data.updatedRawTransactions ?? [];
    const keptIds = new Set(updated.map((r) => r._id));
    const consumedIds = data.rawTransactionIds.filter((id) => !keptIds.has(id));
    if (keepsConsumedRows) {
      const consumed = rows
        .filter((r) => consumedIds.includes(r._id))
        .map((r) => ({...r, remainingAmount: 0}));
      onUpdate({toReplace: [...updated, ...consumed]});
    } else {
      onUpdate({toRemove: consumedIds, toReplace: updated});
    }
    selectionState.clearSelection();
  };

  const handleRawSplit = (rawTransactions: BasicRawTransactionFragment[]) => {
    if (keepsConsumedRows) {
      onUpdate({
        toReplace: rawTransactions.map((r) => ({...r, remainingAmount: 0})),
      });
    } else {
      onUpdate({toRemove: rawTransactions.map((r) => r._id)});
    }
    selectionState.clearSelection();
  };

  const handleDelete = (deleted: string[]) => {
    onUpdate({toRemove: deleted});
    selectionState.clearSelection();
  };

  const handleMoveUpdate = () => {
    // The moved rows leave the source account and join the destination; the
    // modal evicts both accounts' cached list pages so they refetch. Here we
    // only need to drop the now-stale selection.
    selectionState.clearSelection();
  };

  const handleAddTransaction = (
    newTransaction: BasicRawTransactionFragment,
  ) => {
    onUpdate({toAdd: [newTransaction]});
  };

  const handleInverseCreated = (data: {
    created: BasicRawTransactionFragment;
    consumedSourceIds?: string[];
  }) => {
    // A combined inverse arrives fully consumed, so it only belongs on
    // lists that keep processed rows — inserting it into e.g. the review
    // list (which queries includeProcessed: false) would show a row the
    // next refetch takes away.
    if (data.consumedSourceIds == null || keepsConsumedRows) {
      onUpdate({toAdd: [data.created]});
    }
    // Combined into a transfer: the sources are now fully consumed — same
    // list bookkeeping as creating a transaction from the selection.
    if (data.consumedSourceIds) {
      handleCreate({rawTransactionIds: data.consumedSourceIds});
    }
  };

  const handleImport = (imported: BasicRawTransactionFragment[]) => {
    onUpdate({toAdd: imported});
  };

  // Edit is a plain prefilled form for a single row, and the bulk modal for
  // larger selections — there each property must be explicitly activated to
  // apply ("set this field on all of these, touch nothing else"). Both gate
  // per property (synced rows lock date/description, transfers lock
  // payee/category, and so on), so there is nothing to pre-filter here.
  const canEdit = hasSelected;

  const handleEditSaved = (updated: BasicRawTransactionFragment[]) => {
    // In an account-scoped list a moved row leaves the list; otherwise patch
    // rows in place (date/description may have changed, which re-sorts).
    if (accountId) {
      const moved = updated.filter((r) => r.account._id !== accountId);
      const stays = updated.filter((r) => r.account._id === accountId);
      onUpdate({toRemove: moved.map((r) => r._id), toReplace: stays});
    } else {
      onUpdate({toReplace: updated});
    }
    selectionState.clearSelection();
  };

  const handleEdit = () => {
    if (!canEdit) return;
    if (singleSelected) {
      modals.editRawTransactionModal.openModal({
        rawTransaction: selected[0],
        onUpdate: (updated) => handleEditSaved([updated]),
      });
      return;
    }
    modals.bulkEditRawTransactionsModal.openModal({
      rawTransactions: selected,
      onSaved: handleEditSaved,
    });
  };

  // The inverse offsets the selection's net, so the rows must share a
  // currency (sums holds one total per currency) and net to something — a
  // zero net is already balanced and has no inverse.
  const canCreateInverse =
    hasSelected &&
    sums.length === 1 &&
    selected.reduce((sum, r) => sum + r.amount, 0) !== 0;

  const openInverseModal = () => {
    if (!canCreateInverse) return;
    modals.createInverseRawTransactionModal.openModal({
      rawTransactions: selected,
      onCreate: handleInverseCreated,
    });
  };

  const openSplitRawTransactionModal = () => {
    if (!canSplitIntoTransactions) return;
    modals.splitRawTransactionModal.openModal({
      rawTransactions: selected,
      onSplit: handleRawSplit,
    });
  };

  // Attaching consumes each raw in full, and the server rejects a raw that
  // already has consumptions — so every selected row must be untouched. A
  // transaction also holds one currency, so a mixed selection has no valid
  // target (the modal filters candidates to the selection's currency).
  const canAddToTransaction =
    hasSelected &&
    sums.length === 1 &&
    selected.every((r) => (r.remainingAmount ?? r.amount) === r.amount);

  // Splitting works on one or more fresh raw transactions. With several
  // selected, each split consumes every raw proportionally, so they must share
  // a currency and their combined net must be non-zero (a zero net is a
  // transfer, and there is nothing to divide).
  const canSplitIntoTransactions =
    hasSelected &&
    selected.reduce((sum, r) => sum + r.amount, 0) !== 0 &&
    selected.every((r) => r.currency === selected[0].currency) &&
    selected.every((r) => r.amount !== 0) &&
    // Splitting divides the whole row, so every selected row must still be
    // untouched. Null reads as fully unconsumed, same as canTrackCredit.
    selected.every((r) => (r.remainingAmount ?? r.amount) === r.amount);

  // b is "categorize with a benefit": the create modal with its benefit
  // picker already expanded (the benefit rides the create as an attachment).
  // Mirrors the modal's attachment eligibility — one untouched USD raw (the
  // attachments record the raw at face value, so a partially consumed raw
  // can't take one; a single raw with a remainder is never balanced). For
  // any other selection the modal would ignore expandBenefit and open as a
  // plain create, so the benefit-specific shortcut stands down instead, as
  // it did before the flows merged.
  const canCreateWithBenefit =
    canCreateTransaction &&
    singleSelected &&
    selected[0].currency === 'USD' &&
    (selected[0].remainingAmount ?? selected[0].amount) === selected[0].amount;

  // The selection's actions, grouped by what happens to the rows. Allocate:
  // the rows are consumed into a transaction and leave any unallocated/review
  // list — benefit links and fee periods ride Create transaction as
  // attachments in its Tags & Fields strip. Set up: a related record is
  // created (the counterpart leg of a transfer) but the rows stay put, still
  // awaiting allocation. Fix: the raw rows themselves are wrong — correct or
  // remove them.
  const selectionMenuItems = (
    <>
      <PopoverMenuGroupLabel>Allocate</PopoverMenuGroupLabel>
      <PopoverMenuItem
        onSelect={() => openCreateTransactionModal()}
        disabled={!canCreateTransaction}
        iconLeft={<CircleDollarSign />}
      >
        Create transaction
      </PopoverMenuItem>
      <PopoverMenuItem
        onSelect={() =>
          modals.addRawTransactionToPaymentModal.openModal({
            rawTransactions: selected,
            onAttached: (rawTransactionIds) => {
              if (keepsConsumedRows) {
                onUpdate({
                  toReplace: selected
                    .filter((r) => rawTransactionIds.includes(r._id))
                    .map((r) => ({...r, remainingAmount: 0})),
                });
              } else {
                onUpdate({toRemove: rawTransactionIds});
              }
              selectionState.clearSelection();
            },
          })
        }
        disabled={!canAddToTransaction}
        iconLeft={<Link2 />}
      >
        Add to transaction
      </PopoverMenuItem>
      <PopoverMenuItem
        onSelect={openSplitRawTransactionModal}
        disabled={!canSplitIntoTransactions}
        iconLeft={<Split />}
      >
        Split
      </PopoverMenuItem>
      <PopoverMenuItem
        onSelect={() => {
          if (!canTrackCredit) return;
          modals.trackCreditModal.openModal({
            rawTransaction: selected[0],
            onCreate: handleCreate,
          });
        }}
        disabled={!canTrackCredit}
        iconLeft={<Archive />}
      >
        Track as credit or loan
      </PopoverMenuItem>
      <PopoverMenuDivider />
      <PopoverMenuGroupLabel>Set up</PopoverMenuGroupLabel>
      <PopoverMenuItem
        onSelect={openInverseModal}
        disabled={!canCreateInverse}
        iconLeft={<ArrowLeftRight />}
      >
        Create inverse
      </PopoverMenuItem>
      <PopoverMenuDivider />
      <PopoverMenuGroupLabel>Fix</PopoverMenuGroupLabel>
      {/* The line's own page: its allocations and the source data (Plaid,
          EZPass) it was synced from. A tap opens the transaction instead. */}
      <PopoverMenuItem
        onSelect={() => {
          if (!singleSelected) return;
          const id = selected[0]._id;
          selectionState.clearSelection();
          navigate(`/raw-transactions/${id}`);
        }}
        disabled={!singleSelected}
        iconLeft={<FileText />}
      >
        Details
      </PopoverMenuItem>
      <PopoverMenuItem
        onSelect={handleEdit}
        disabled={!canEdit}
        iconLeft={<Edit2 />}
      >
        Edit
      </PopoverMenuItem>
      <PopoverMenuItem
        onSelect={() =>
          modals.updateRawTransactionsModal.openModal({
            rawTransactions: selected,
            onUpdate: handleMoveUpdate,
          })
        }
        disabled={!hasSelected}
        iconLeft={<FolderInput />}
      >
        Move
      </PopoverMenuItem>
      <PopoverMenuItem
        onSelect={openDeleteRawTransactionModal}
        disabled={!hasSelected}
        iconLeft={<Trash2 />}
      >
        Delete
      </PopoverMenuItem>
    </>
  );

  return {
    selected,
    sums,
    selectedCount,
    hasSelected,
    singleSelected,
    anyModalOpen,
    canCreateTransaction,
    canCreateWithBenefit,
    openCreateTransactionModal,
    openCreateForRow,
    canEdit,
    handleEdit,
    handleCreate,
    handleAddTransaction,
    handleImport,
    selectionMenuItems,
  };
};
